import {ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnDestroy, OnInit} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {Router} from '@angular/router';
import {ECheckoutStatus} from '@app/checkout/enums/checkout-status.enum';
import {CheckoutStep} from '@app/checkout/enums/checkout-step.enum';
import {EGender, EGenderLabel} from '@app/checkout/enums/gender.enum';
import {OrderStatusEnum} from '@app/checkout/enums/order-status.enum';
import {ICheckoutComponent} from '@app/checkout/models/checkout-component.model';
import {Order} from '@app/checkout/models/order-request.model';
import {IPatientRelation} from '@app/checkout/models/patient-relation.model';
import {ISampleType} from '@app/checkout/models/sample-type.model';
import {
  getPatientRelationTypes,
  getSampleTypesFromPatientInfoPage,
  resetCheckoutStatus,
  updateOrderFromPatientInfo,
} from '@app/checkout/state/checkout.actions';
import {
  selectCheckoutStatus,
  selectOrderForm,
  selectPatientRelationTypes,
  selectSampleTypes,
} from '@app/checkout/state/checkout.selector';
import {DateUtils} from '@app/core/utils/date.util';
import {Email_Regex, OnlyDigits_Regex, SampleTypes} from '@app/shared/constants';
import {ICountry} from '@app/shared/models/country.model';
import {StepperService} from '@app/shared/services/stepper.service';
import {CustomValidators} from '@app/shared/utils/custom-validators.utils';
import {selectCountries} from '@app/signUp/state/sign-up.selector';
import {Store} from '@ngrx/store';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {ConfirmationService, MessageService, ToastMessageOptions} from 'primeng/api';
import {map, Observable, Subject, takeUntil, tap} from 'rxjs';
import {ValidationService} from 'src/app/shared/services/validation.service';
import {IPatientInfo, IRelatedPatientInfo} from '../../models/patient-info.model';

import {AsyncPipe} from '@angular/common';
import {ErrorHandlerUtils} from '@app/core/utils/error-handler.utils';
import {Bind} from 'primeng/bind';
import {Divider} from 'primeng/divider';
import {InputText} from 'primeng/inputtext';
import {Message} from 'primeng/message';
import {Select} from 'primeng/select';
import {MaskedCalendarComponent} from '../../../shared/components/masked-calendar/masked-calendar.component';

@Component({
  selector: 'app-patient-info',
  templateUrl: './patient-info.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    Bind,
    InputText,
    MaskedCalendarComponent,
    Select,
    Divider,
    Message,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class PatientInfoComponent implements OnInit, OnDestroy, ICheckoutComponent {
  private readonly formBuilder = inject(FormBuilder);
  private readonly validationService = inject(ValidationService);
  private readonly store = inject(Store);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly router = inject(Router);
  private readonly stepperService = inject(StepperService);
  private readonly translate = inject(TranslateService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly messageService = inject(MessageService);

  private readonly unsubscribe$ = new Subject<void>();
  patientInfoForm: FormGroup<IPatientInfo>;
  relatedPatientForms: FormArray;
  maxDate = new Date();
  productId?: number;
  orderData: Order | undefined;
  checkoutState: ECheckoutStatus | undefined;
  checkoutStatus = ECheckoutStatus;
  exceedTestLimit = '';
  allowCharecter = '';
  allowAlphanumeric = '';
  allowDigitChar = '';
  allowLetterDiacritics = '';
  sampleTypeLookup = SampleTypes;
  countries$: Observable<ICountry[]>;
  genderOptions: {label: EGenderLabel; value: EGender}[] = [
    {label: EGenderLabel.Male, value: EGender.Male},
    {label: EGenderLabel.Female, value: EGender.Female},
    {label: EGenderLabel.Unknown, value: EGender.Unknown},
    {label: EGenderLabel.Other, value: EGender.Other},
  ];
  nextInProgress = false;
  previousInProgress = false;
  cancelInProgress = false;
  draftInProgress = false;

  sampleTypes$: Observable<ISampleType[]>;
  patientRelationTypes$: Observable<IPatientRelation[]>;
  messages: ToastMessageOptions[] = [
    {
      severity: 'info',
      summary: this.translate.instant('Common.Info'),
      detail: this.translate.instant('Patient_Info.RelatedPatientInfo'),
      icon: 'pi pi-info-circle',
    },
  ];

  activePatientRelation = true;
  globalToast = 'global-toast';

  constructor() {
    this.patientInfoForm = this.formBuilder.group(
      {
        firstName: ['', [Validators.required, this.validationService.charactersDiacriticsWithMaxLimt]],
        lastName: ['', [Validators.required, this.validationService.charactersDiacriticsWithMaxLimt]],
        dob: new FormControl<Date | null>(null, [Validators.required, CustomValidators.maxDate(this.maxDate)]),
        patientId: ['', [this.validationService.specialCharacterWithDigits]],
        sampleType: new FormControl<number | null>(null, Validators.required),
        gender: ['', Validators.required],
        email: ['', Validators.pattern(Email_Regex)],
        countryCode: [''],
        phoneNumber: ['', [Validators.pattern(OnlyDigits_Regex)]],
        barcode: ['', [Validators.required]],
        sampleCollectionDate: new FormControl<Date | null>(null, [
          Validators.required,
          CustomValidators.maxDate(this.maxDate),
        ]),
        relatedPatientInfo: this.formBuilder.array([]),
      },
      {
        validators: [
          this.uniqueBarcodeValidator(),
          CustomValidators.requireBothOrNeitherValidator('phoneNumber', 'countryCode'),
        ],
      }
    );

    this.relatedPatientForms = this.patientInfoForm.get('relatedPatientInfo') as FormArray;

    this.countries$ = this.store.select(selectCountries).pipe(
      takeUntil(this.unsubscribe$),
      map(countries => {
        const flattenCountries = [...(countries ?? [])].sort((a, b) =>
          a.twoLetterIsoCode.localeCompare(b.twoLetterIsoCode)
        );
        return (
          flattenCountries.map(country => ({
            ...country,
            phoneNumberPrefixWithPlus: `(${country.twoLetterIsoCode}) +${country.phoneNumberPrefix}`,
          })) ?? []
        );
      })
    );

    this.listenToOrderFormData();

    this.store
      .select(selectCheckoutStatus)
      .pipe(
        takeUntil(this.unsubscribe$),
        tap((checkoutStatus: ECheckoutStatus) => {
          this.checkoutState = checkoutStatus;
          if (checkoutStatus === ECheckoutStatus.SuccessFromPatientInfo) {
            this.patientInfoForm.markAsPristine();
            this.stepperService.emitNext(CheckoutStep.ClinicalHistory);
          }
          if (
            checkoutStatus === ECheckoutStatus.SuccessFromPatientInfo ||
            checkoutStatus === ECheckoutStatus.ErrorFromPatientInfo
          ) {
            this.nextInProgress = false;
            this.draftInProgress = false;
            this.cdr.markForCheck();
          }
        })
      )
      .subscribe();
    this.translateStaticText();

    this.patientInfoForm.valueChanges.subscribe(() => {
      this.updateRequiredState();
    });

    this.updateRequiredState();

    this.sampleTypes$ = this.store.select(selectSampleTypes);
    this.patientRelationTypes$ = this.store.select(selectPatientRelationTypes);
  }

  private listenToOrderFormData() {
    this.store
      .select(selectOrderForm)
      .pipe(
        takeUntil(this.unsubscribe$),
        tap((order: Order) => {
          this.setOrderAndProduct(order);
          this.updatePatientForm();
          this.createRelatedPatientForms(
            this.orderData?.relatedPatientsList,
            this.orderData?.orderItems[0]?.orderItemAttributes[0]?.noPatients
          );
          this.dispatchForSampleTypes();
        })
      )
      .subscribe();
  }

  private dispatchForSampleTypes() {
    this.store.dispatch(
      getSampleTypesFromPatientInfoPage({
        productId: this.productId ?? 0,
      })
    );
  }

  private setOrderAndProduct(order: Order) {
    this.orderData = order;
    this.productId = this.orderData?.orderItems[0]?.productId ?? this.orderData?.productId;
  }

  private updatePatientForm() {
    this.patientInfoForm.patchValue({
      firstName: this.orderData?.patientFirstName,
      lastName: this.orderData?.patientLastName,
      dob: this.orderData?.patientDateOfBirth ? new Date(this.orderData?.patientDateOfBirth) : null,
      patientId: this.orderData?.patientMedicalRecordNumber,
      sampleType: this.orderData?.sample?.sampleTypeId ?? this.orderData?.patientSampleTypeId,
      email: this.orderData?.patientEmail,
      gender: this.orderData?.patientGender,
      countryCode: this.orderData?.patientPrefixPhoneNumber,
      phoneNumber: this.orderData?.patientPhoneNumber,
      barcode: this.orderData?.sample?.barcode ?? this.orderData?.barcode,
      sampleCollectionDate: this.orderData?.sample?.collectionDate
        ? new Date(this.orderData?.sample?.collectionDate)
        : null,
    });
  }

  ngOnInit(): void {
    this.store.dispatch(getPatientRelationTypes({enabled: this.activePatientRelation}));
  }

  createRelatedPatientForms(relatedPatients?: IRelatedPatientInfo[], noOfPatients: number = 0): void {
    this.relatedPatientForms.clear();
    if (relatedPatients && relatedPatients.length > 0) {
      this.updateRelatedPatientForms(relatedPatients);
    } else {
      for (let n = 0; n < noOfPatients - 1; n++) {
        this.addRelatedPatientInfo();
      }
    }
  }

  updateRelatedPatientForms(relatedPatients: IRelatedPatientInfo[]): void {
    for (const relatedPatient of relatedPatients) {
      const dob = relatedPatient?.dateOfBirth === null ? null : new Date(relatedPatient?.dateOfBirth);

      const sampleCollectionDate =
        relatedPatient?.collectionDate === null ? null : new Date(relatedPatient?.collectionDate);

      this.relatedPatientForms.push(
        this.formBuilder.group({
          sampleId: [relatedPatient.sampleId],
          patientId: [relatedPatient.patientId],
          firstName: [
            relatedPatient.firstName,
            [Validators.required, this.validationService.charactersDiacriticsWithMaxLimt],
          ],
          lastName: [
            relatedPatient.lastName,
            [Validators.required, this.validationService.charactersDiacriticsWithMaxLimt],
          ],
          dob: new FormControl<Date | null>(dob, [Validators.required, CustomValidators.maxDate(this.maxDate)]),
          patientRelationType: new FormControl<number | null>(
            relatedPatient.patientRelationTypeId,
            Validators.required
          ),
          sampleType: new FormControl<number | null>(relatedPatient.sampleTypeId, Validators.required),
          barcode: [relatedPatient.barcode, [Validators.required]],
          sampleCollectionDate: new FormControl<Date | null>(sampleCollectionDate, [
            Validators.required,
            CustomValidators.maxDate(this.maxDate),
          ]),
        })
      );
    }
  }

  addRelatedPatientInfo(): void {
    this.relatedPatientForms.push(
      this.formBuilder.group({
        sampleId: [''],
        patientId: [''],
        firstName: ['', [Validators.required, this.validationService.charactersDiacriticsWithMaxLimt]],
        lastName: ['', [Validators.required, this.validationService.charactersDiacriticsWithMaxLimt]],
        dob: new FormControl<Date | null>(null, [Validators.required, CustomValidators.maxDate(this.maxDate)]),
        patientRelationType: new FormControl<number | null>(null, Validators.required),
        sampleType: new FormControl<number | null>(null, Validators.required),
        barcode: ['', [Validators.required]],
        sampleCollectionDate: new FormControl<Date | null>(null, [
          Validators.required,
          CustomValidators.maxDate(this.maxDate),
        ]),
      })
    );
  }

  updateRequiredState() {
    const phone = this.patientInfoForm.get('phoneNumber')?.value;
    const countryCode = this.patientInfoForm.get('countryCode')?.value;

    if (!!phone || !!countryCode) {
      this.patientInfoForm.get('countryCode')?.setValidators([Validators.required]);
      this.patientInfoForm
        .get('phoneNumber')
        ?.setValidators([
          Validators.pattern(OnlyDigits_Regex),
          Validators.required,
          Validators.minLength(4),
          Validators.maxLength(13),
        ]);
    } else {
      this.patientInfoForm.get('countryCode')?.setValidators(null);
      this.patientInfoForm.get('phoneNumber')?.setValidators([Validators.pattern(OnlyDigits_Regex)]);
    }
    this.cdr.markForCheck();
  }

  isRequired(fieldName: string) {
    return !!this.patientInfoForm.get(fieldName)?.hasValidator(Validators.required);
  }

  disableNext() {
    return this.patientInfoForm.invalid;
  }

  next() {
    const lastCompletedStep = this.orderData?.lastCompletedStep ?? -1;
    if (this.patientInfoForm.dirty || lastCompletedStep < CheckoutStep.PatientInfo) {
      this.nextInProgress = true;
      if (this.orderData) {
        this.orderData = {
          ...this.orderData,
          lastCompletedStep:
            lastCompletedStep > CheckoutStep.PatientInfo && this.orderData?.orderItems[0]?.code !== 'SOLT'
              ? lastCompletedStep
              : CheckoutStep.PatientInfo,
          currentStep: CheckoutStep.ClinicalHistory,
          orderStatusId: OrderStatusEnum.New.toString(),
        };
      }
      this.store.dispatch(resetCheckoutStatus());
      this.saveOrder();
    } else {
      this.stepperService.emitNext(CheckoutStep.ClinicalHistory);
    }
  }

  previous() {
    this.stepperService.emitPrevious(CheckoutStep.BillingInfo);
  }

  cancelOrder() {
    // use for custom cancel logic
  }

  saveDraftOrder() {
    if (
      this.orderData &&
      this.orderData.lastCompletedStep >= CheckoutStep.PatientInfo &&
      this.patientInfoForm.invalid
    ) {
      ErrorHandlerUtils.showErrorMessage(
        this.messageService,
        this.globalToast,
        this.translate.instant('Checkout.InvalidFormMessage')
      );
      return;
    }
    this.draftInProgress = true;
    if (this.orderData) {
      this.orderData = {
        ...this.orderData,
        orderStatusId: OrderStatusEnum.New.toString(),
      };
    }
    this.saveOrder(true);
  }

  nextLoading() {
    return this.nextInProgress;
  }

  previousLoading() {
    return this.previousInProgress;
  }

  cancelLoading() {
    return this.cancelInProgress;
  }

  draftOrSubmitLoading() {
    return this.draftInProgress;
  }

  saveOrder(isDraft = false) {
    let dobUtc: Date | null = null;
    const dob = this.patientInfoForm.get('dob')?.value;
    if (dob) {
      dobUtc = DateUtils.getUTCDate(new Date(dob));
    }

    let cdUtc: Date | null = null;
    const cd = this.patientInfoForm.get('sampleCollectionDate')?.value;
    if (cd) {
      cdUtc = DateUtils.getUTCDate(new Date(cd));
    }

    const sample = {
      ...this.orderData?.sample,
      sampleTypeId: this.patientInfoForm.controls.sampleType.value,
      barcode: this.patientInfoForm.controls.barcode.value,
      collectionDate: cdUtc,
    };
    let orderData = {
      ...this.orderData,
      productId: this.orderData?.orderItems[0]?.productId ?? this.orderData?.productId,
      patientFirstName: this.patientInfoForm.get('firstName')?.value ?? '',
      patientLastName: this.patientInfoForm.get('lastName')?.value ?? '',
      patientDateOfBirth: dobUtc,
      patientMedicalRecordNumber: this.patientInfoForm.get('patientId')?.value ?? '',
      patientSampleTypeId: this.patientInfoForm.controls.sampleType.value,
      currentStep: isDraft ? this.orderData?.currentStep : CheckoutStep.PatientInfo,
      patientEmail: this.patientInfoForm.controls.email.value,
      patientGender: this.patientInfoForm.controls.gender.value,
      sample: sample,
    } as Order;

    const phoneNumber = this.patientInfoForm.controls.phoneNumber.value;
    const countryCode = this.patientInfoForm.controls.countryCode.value;
    if (isDraft || (phoneNumber && countryCode)) {
      orderData.patientPhoneNumber = phoneNumber ?? '';
      orderData.patientPrefixPhoneNumber = countryCode ?? '';
    }
    if (this.relatedPatientForms.controls.length > 0) {
      orderData = this.processRelatedPatient(orderData);
    }

    this.store.dispatch(updateOrderFromPatientInfo({orderRequest: orderData, isDraft: isDraft}));
  }

  processRelatedPatient(orderData: Order): Order {
    const relatePatientList = [];
    for (const relatedPatient of this.relatedPatientForms.controls) {
      const relatedPatientInfo = {
        patientRelationTypeId: relatedPatient.get('patientRelationType')?.value,
        firstName: relatedPatient.get('firstName')?.value,
        lastName: relatedPatient.get('lastName')?.value,
        dateOfBirth: relatedPatient.get('dob')?.value,
        sampleTypeId: relatedPatient.get('sampleType')?.value,
        barcode: relatedPatient.get('barcode')?.value,
        collectionDate: relatedPatient.get('sampleCollectionDate')?.value,
      } as IRelatedPatientInfo;

      const patientId = relatedPatient.get('patientId')?.value;
      if (patientId && patientId > 0) {
        relatedPatientInfo.patientId = Number(patientId);
      }

      const sampleId = relatedPatient.get('sampleId')?.value;
      if (sampleId && sampleId > 0) {
        relatedPatientInfo.sampleId = Number(sampleId);
      }

      relatePatientList.push(relatedPatientInfo);
    }
    orderData.relatedPatientsList = relatePatientList;

    return orderData;
  }

  private uniqueBarcodeValidator(): ValidatorFn {
    return (formGroup: AbstractControl) => {
      const patientBarcode = formGroup.get('barcode')?.value?.trim();
      if (patientBarcode && patientBarcode?.trim() !== '') {
        formGroup.get('barcode')?.setErrors(null);
      } else {
        formGroup.get('barcode')?.setErrors({required: true});
      }
      const patientsFormArray = formGroup.get('relatedPatientInfo') as FormArray;
      patientsFormArray.controls.forEach(control => {
        const barcode = control.get('barcode')?.value?.trim();
        if (barcode && barcode !== '') {
          const filteredControls = patientsFormArray.controls.filter(x => x.get('barcode')?.value?.trim() === barcode);

          if (barcode === patientBarcode) {
            formGroup.get('barcode')?.setErrors({notUnique: true});
            control.get('barcode')?.setErrors({notUnique: true});
          }
          if (filteredControls.length > 1) {
            filteredControls.forEach(filteredControl => {
              filteredControl.get('barcode')?.setErrors({notUnique: true});
            });
          }
          if (barcode !== patientBarcode && filteredControls.length === 1) {
            control.get('barcode')?.setErrors(null);
          }
        } else {
          control.get('barcode')?.setErrors({required: true});
        }
      });
      return null;
    };
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private translateStaticText() {
    this.exceedTestLimit = this.translate.instant('SignUp.ExceedTestLimit');
    this.allowCharecter = this.translate.instant('SignUp.AllowCharecter');
    this.allowAlphanumeric = this.translate.instant('SignUp.AllowAlphanumeric');
    this.allowDigitChar = this.translate.instant('SignUp.AllowDigitChar');
    this.allowLetterDiacritics = this.translate.instant('SignUp.AllowLetterDiacritics');
  }
}
